import { useEffect, useState } from 'react'
import clsx from 'clsx'
import styles from './controls.module.css'
import FacetHistogram from './FacetHistogram'
import { HOW_WE_TEST_URL } from 'lib/dealhunt/constants'
import type { ScoreFilterGroup } from 'lib/dealhunt/types'

interface ScoreControlProps {
  group: ScoreFilterGroup
  onCommit: (min: number | null) => void
}

/** "MDAQS OVERALL" → "MDAQS Overall": the acronym stays, the rest reads as prose. */
function proseName(caption: string): string {
  return caption
    .split(' ')
    .map(w => (w === 'MDAQS' ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ')
}

/** MDAQS is a lab measurement readers don't know by name — every MDAQS axis gets a
 *  "how we test" affordance next to its caption (the Sound axis carries the
 *  "MDAQS OVERALL" tech caption; the sub-axes carry the word in their label). */
export function howWeTestFor(group: ScoreFilterGroup): { href: string; title: string } | null {
  const name = group.tech ?? group.label
  if (!/mdaqs/i.test(name)) {
    return null
  }
  return { href: HOW_WE_TEST_URL, title: `How we test — what ${proseName(name)} measures` }
}

/** A SoundGuys score axis as an "at least" slider on its fixed scale, with the
 *  class distribution and the backend's coverage sentence. */
const ScoreControl: React.VFC<ScoreControlProps> = ({ group, onCommit }) => {
  const { scale, stats } = group
  const showTech = group.tech !== null && group.tech.toLowerCase() !== group.label.toLowerCase()
  const howWeTest = howWeTestFor(group)
  const appliedMin = group.applied?.min ?? null

  const span = scale.max - scale.min || 1
  const buckets = stats.distribution.length
  const bucketWidth = buckets > 0 ? span / buckets : span

  /**
   * Fitted track: the slider's floor comes from the first occupied bucket,
   * snapped down to a clean half-point — so drag precision lives where the
   * products do (over-ears' noise cancelling occupies 5.6–9.4 of a 0–10 scale;
   * a full-scale track leaves half of every drag dead). The scale's max stays
   * anchored: "out of 10" is part of the reading. An applied value below the
   * fitted floor (other filters reshaped the distribution after it was set)
   * widens the domain rather than stranding the thumb.
   */
  const firstOccupied = stats.distribution.findIndex(c => c > 0)
  const snap = Math.max(scale.step, 0.5)
  const fittedMin =
    firstOccupied <= 0
      ? scale.min
      : Math.max(scale.min, Math.floor((scale.min + firstOccupied * bucketWidth) / snap) * snap)
  const domainMin = appliedMin !== null ? Math.min(fittedMin, appliedMin) : fittedMin
  const domainSpan = scale.max - domainMin || 1
  /** Buckets wholly below the fitted floor are trimmed from the chart. */
  const cut = Math.floor(((domainMin - scale.min) / span) * buckets)
  const shownDistribution = stats.distribution.slice(cut)

  const [value, setValue] = useState(appliedMin ?? domainMin)
  useEffect(() => {
    setValue(appliedMin ?? domainMin)
  }, [appliedMin, domainMin])

  const bucketOf = (v: number): number =>
    Math.max(0, Math.min(buckets - 1, Math.floor(((v - scale.min) / span) * buckets)))
  const fmt = (v: number): string => v.toFixed(scale.decimals)

  /** Products scoring at least `v`, interpolated inside the cut bucket — a
   *  bucket-resolution estimate for the drag readout; the committed filter
   *  re-queries the API and is exact. */
  const atLeast = (v: number): number =>
    Math.round(
      stats.distribution.reduce((sum, count, i) => {
        const lo = scale.min + i * bucketWidth
        const hi = lo + bucketWidth
        if (v <= lo) {
          return sum + count
        }
        return v < hi ? sum + (count * (hi - v)) / bucketWidth : sum
      }, 0)
    )

  const active = appliedMin !== null
  const readout =
    value <= domainMin ? 'any' : `${fmt(value)}+ · ${atLeast(value)} of ${stats.count}`

  return (
    <>
      {(showTech || howWeTest) && (
        <div className={styles.tech}>
          {showTech && group.tech}
          {howWeTest && (
            <a
              className={styles.techLink}
              href={howWeTest.href}
              target="_blank"
              rel="noopener noreferrer"
              title={howWeTest.title}
              aria-label={howWeTest.title}
            >
              <svg
                className={styles.techIcon}
                viewBox="0 0 16 16"
                aria-hidden="true"
                focusable="false"
              >
                <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.4" />
                <circle cx="8" cy="5" r="0.9" fill="currentColor" />
                <path
                  d="M8 7.2v4.3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </a>
          )}
        </div>
      )}
      {buckets > 0 && (
        <FacetHistogram
          distribution={shownDistribution}
          activeFrom={Math.max(0, bucketOf(value) - cut)}
          activeTo={shownDistribution.length - 1}
          compact
        />
      )}
      <div className={styles.sliderLabels}>
        <span>{fmt(domainMin)}</span>
        <span>{fmt(scale.max)}</span>
      </div>
      <div className={styles.slider}>
        <div className={styles.sliderTrack} />
        <div
          className={styles.sliderFill}
          style={{ left: `${((value - domainMin) / domainSpan) * 100}%`, right: 0 }}
        />
        <input
          type="range"
          className={styles.sliderInput}
          min={domainMin}
          max={scale.max}
          step={scale.step}
          value={value}
          aria-label={`${group.label} minimum score`}
          onChange={e => setValue(Number(e.target.value))}
          onPointerUp={() => onCommit(value <= domainMin ? null : value)}
          onKeyUp={() => onCommit(value <= domainMin ? null : value)}
        />
      </div>
      <div className={styles.readoutRow}>
        <span className={styles.foot}>{group.copy.sentence}</span>
        <span className={clsx(styles.readout, { [styles.readoutActive]: active })}>{readout}</span>
      </div>
    </>
  )
}

export default ScoreControl
